import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de liquidaciones (`liquidaciones`) para el panel de staff
 * (Control de Horarios y Liquidación de Sueldos, slice 5 — Liquidación).
 * SOLO LECTURA desde el navegador: la tabla es append-only, con únicamente
 * una política de SELECT admin (`liquidaciones_admin_read`, migración 20) y
 * SIN política de INSERT/UPDATE/DELETE. El único escritor es la RPC
 * `create_liquidacion` (SECURITY DEFINER, `revoke ... from public, anon,
 * authenticated` + `grant to service_role`) — este repositorio, que corre
 * con la anon key del navegador, no tiene grant para llamarla ni para
 * escribir la tabla directo; la escritura real vive en `api/liquidacion.js`
 * (service_role, PR 5c).
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`), mismo patrón que
 * `fichadas.repository.js`/`empleados.repository.js`.
 */

// Unit B1 (liquidacion-anulacion, design.md AD13): los tres campos de
// auditoria de la anulacion (migracion 27) — el historial (B2) los necesita
// para mostrar motivo/fecha de la anulacion, y `estado` ya venia incluido
// desde antes (ahora puede valer 'anulada', no solo 'confirmada').
const LIQUIDACION_COLUMNS =
    'id, empleado_id, periodo_desde, periodo_hasta, bruto, consumos, adelantos, neto, estado, detalle, policy_version, multiplicador_feriado, creada_por, creada_at, anulada_at, anulada_por, anulada_motivo, empleados(nombre)';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que fichadas.repository.js/empleados.repository.js).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Liquidaciones (confirmadas y anuladas), más recientes primero
 * (`creada_at` desc, y `id` desc para desempatar: dos filas creadas en el
 * mismo instante quedan siempre en el mismo orden, así una página de "Ver
 * más" nunca saltea ni repite una fila). Todos los filtros son opcionales,
 * se combinan, y miran SIEMPRE el inicio del período (`periodo_desde`) —
 * una liquidación pertenece al mes en que arranca (`mesDeLiquidacion`,
 * `js/shared/payroll-policy.js`; una semana que cruza de mes va al PRIMER
 * mes). Nunca hay cota sobre `periodo_hasta` (L1: una liquidación cuyo
 * período termina en el futuro tiene que seguir apareciendo):
 *
 * - `inicioDesde`: `periodo_desde >= inicioDesde` — cota inferior de la
 *   ventana del historial, y del mes del export CSV (L6).
 * - `inicioHasta`: `periodo_desde <= inicioHasta` — cota superior del mes
 *   del export CSV (L6).
 * - `inicioAntesDe` (L8): `periodo_desde < inicioAntesDe` — las liquidaciones
 *   más viejas que la ventana del historial, para "Ver más".
 * - `offset`/`limit` (L8): una página (`range`), sólo si se pasa `limit`.
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que
 * `listFichadas`/`listEmpleados`).
 * @param {{ inicioDesde?: string, inicioHasta?: string, inicioAntesDe?: string, offset?: number, limit?: number }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listLiquidaciones({ inicioDesde, inicioHasta, inicioAntesDe, offset = 0, limit } = {}) {
    const client = getSupabaseClient();
    if (!client) return [];

    let query = client.from('liquidaciones').select(LIQUIDACION_COLUMNS).order('creada_at', { ascending: false }).order('id', { ascending: false });
    if (inicioDesde) query = query.gte('periodo_desde', inicioDesde);
    if (inicioHasta) query = query.lte('periodo_desde', inicioHasta);
    if (inicioAntesDe) query = query.lt('periodo_desde', inicioAntesDe);
    if (Number.isInteger(limit) && limit > 0) query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;
    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Liquidaciones CONFIRMADAS de UN empleado cuyo período se superpone con
 * `[desde, hasta]` (fechas ART `YYYY-MM-DD`, inclusive) — la única lectura
 * que hace la guarda F-E (owner decision 2026-09-27) antes de una corrección
 * de fichada del admin. Columnas mínimas: la guarda solo necesita el período.
 * La superposición exacta por rango (antes/después) la decide la política
 * (`fichadas-ui-policy.js#liquidacionQueCubre`); acá solo se acota la lectura.
 * @param {{ empleadoId: string, desde: string, hasta: string }} params
 * @returns {Promise<Array<{ id: string, empleado_id: string, periodo_desde: string, periodo_hasta: string, estado: string }>>}
 * @throws {Error} si falta un parámetro (error de programación) o Supabase responde con error
 */
export async function listLiquidacionesConfirmadasDeEmpleado({ empleadoId, desde, hasta } = {}) {
    if (!empleadoId || !desde || !hasta) {
        throw new Error('listLiquidacionesConfirmadasDeEmpleado: empleadoId, desde y hasta son requeridos');
    }
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('liquidaciones')
        .select('id, empleado_id, periodo_desde, periodo_hasta, estado')
        .eq('empleado_id', empleadoId)
        .eq('estado', 'confirmada')
        .lte('periodo_desde', hasta)
        .gte('periodo_hasta', desde);
    if (error) rethrow(error);
    return data ?? [];
}
